import { configuration } from "./Configuration";
import { jwtAuthService } from "./JWTAuthService";
import {
  BrowserActionListSchema,
  type BrowserActionList,
} from "./lib/bpm-protocol";
import { decodeXmlEntities } from "./lib/decodeXmlEntities";

/**
 * Client-bot dispatch — submits a TaskAssignment to the locally-installed
 * Chrome extension that hosts the "browser bot" runtime, matching the contract
 * implemented by the sibling `tri-bot` repo's `apps/test-client/src/transport.ts`.
 *
 * The shape of `TaskAssignment` is the one defined in
 * `@the-real-insight/bpm-protocol` (see tri-bot/packages/bpm-protocol/src/index.ts).
 * We don't depend on that package here — the portal just builds and sends a
 * plain object; the extension validates against the schema on the receiving end.
 */

export type ClientBotApprovalPolicy = "auto" | "ask" | "deny-sensitive";

interface TaskAssignment {
  taskId: string;
  processInstanceId: string;
  instruction: string;
  processVariables: Record<string, unknown>;
  context: {
    userId: string;
    tenantId: string;
    locale: string;
    timezone: string;
  };
  callback: { url: string; token: string };
  llmProxy: { baseUrl: string; sessionToken: string; model?: string };
  approvalPolicy: ClientBotApprovalPolicy;
  deadlineMs?: number;
  browserActions?: BrowserActionList;
}

interface ClientBotConfig {
  /** Chrome extension ID hosting the browser-bot runtime. */
  extensionId: string;
  /** Public callback URL (the bot posts events here). */
  callbackUrl: string;
  /** Bearer token the bot uses on the callback. */
  callbackToken: string;
  /** LLM bridge base URL exposed by tri-server. */
  llmProxyBaseUrl: string;
  approvalPolicy: ClientBotApprovalPolicy;
}

// CRA / webpack-DefinePlugin replaces `process.env.REACT_APP_*` only when the
// reference appears verbatim in source. Reading via an alias variable
// (`env.REACT_APP_*`) silently yields undefined at runtime — so each var is
// referenced directly here.
export function getClientBotConfig(): ClientBotConfig {
  const llmBridgePath = process.env.REACT_APP_TRI_BOT_LLM_BRIDGE_PATH || "/llm-bridge/v1";
  const llmProxyBaseUrl =
    process.env.REACT_APP_TRI_BOT_LLM_PROXY_BASE_URL ||
    `${configuration.serverUrl.replace(/\/+$/, "")}${llmBridgePath}`;
  return {
    extensionId: process.env.REACT_APP_TRI_BOT_EXTENSION_ID || "",
    callbackUrl:
      process.env.REACT_APP_TRI_BOT_CALLBACK_URL ||
      `${configuration.serverUrl.replace(/\/+$/, "")}/bot/callback`,
    // bpm-protocol's Zod schema requires `callback.token` to be a non-empty
    // string, so fall back to a placeholder when no shared secret is set
    // (matches the test-client default and keeps local dev unblocked).
    callbackToken: process.env.REACT_APP_TRI_BOT_CALLBACK_TOKEN || "dev-callback-token",
    llmProxyBaseUrl,
    approvalPolicy:
      (process.env.REACT_APP_TRI_BOT_APPROVAL_POLICY as ClientBotApprovalPolicy | undefined) ||
      "ask",
  };
}

export interface ClientBotSubmitInput {
  /** The activeTask object (carries _id, instanceId/processInstanceId, triAttrs, …). */
  activeTask: any;
  /** Authenticated user (jotai authentication atom). */
  authentication: { id?: string; customer?: string };
  /** Optional process variables (data pool flattened into a single map). */
  processVariables?: Record<string, unknown>;
}

export interface ClientBotSubmitResult {
  ok: boolean;
  runId?: string;
  error?: string;
}

/**
 * Build a TaskAssignment from the conversation's active task and dispatch it
 * to the configured Chrome extension. Returns `{ ok: true, runId }` on accept,
 * `{ ok: false, error }` otherwise.
 */
export async function submitClientBotTask(
  input: ClientBotSubmitInput,
): Promise<ClientBotSubmitResult> {
  const { activeTask, authentication, processVariables } = input;

  // chrome.runtime is only injected into pages whose origin appears in the
  // extension manifest's `externally_connectable.matches`. If it's missing
  // here the most likely cause is that the portal's origin hasn't been
  // whitelisted in `tri-bot/apps/browser-bot/manifest.config.ts`. Surface
  // that hint instead of a generic "not installed" message.
  const chromeRuntime = (globalThis as any).chrome?.runtime;
  if (!chromeRuntime?.sendMessage) {
    const origin = typeof window !== "undefined" ? window.location.origin : "this page";
    return {
      ok: false,
      error:
        `Chrome extension runtime not exposed to ${origin}. ` +
        `Add "${origin}/*" to externally_connectable.matches in the TRI Bot extension manifest, ` +
        `rebuild & reload the extension at chrome://extensions, then reload this page.`,
    };
  }

  const cfg = getClientBotConfig();
  if (!cfg.extensionId) {
    return {
      ok: false,
      error: "REACT_APP_TRI_BOT_EXTENSION_ID is not configured.",
    };
  }

  const sessionToken = jwtAuthService.getAccessToken();
  if (!sessionToken) {
    return { ok: false, error: "Not signed in." };
  }

  const triAttrs = activeTask?.triAttrs ?? {};
  const taskUxElement: string | undefined =
    triAttrs.uxElement ?? activeTask?.extensions?.["tri:uxElement"] ?? activeTask?.uxElement;
  const isBrowserBotTask = taskUxElement === "browser-bot";
  const instruction: string =
    triAttrs.botPrompt ||
    activeTask?.botPrompt ||
    triAttrs.userPrompt ||
    activeTask?.userPrompt ||
    activeTask?.name ||
    "";
  if (!instruction) {
    return { ok: false, error: "No bot prompt set on the active task." };
  }

  // Recorded walkthrough for browser-bot tasks. Parse failures are now
  // always non-fatal — the task proceeds without the walkthrough rather
  // than dead-ending the bot. The original spec called for strict
  // rejection on browser-bot, but that turns leftover/stale data from
  // earlier authoring into a wall the modeler can't get past without
  // hand-editing the BPMN XML. Better UX: surface the issue as a
  // console warning, run the bot with just the prompt, let the modeler
  // re-record or clean the data when convenient.
  let browserActions: BrowserActionList | undefined;
  if (isBrowserBotTask) {
    const rawBrowserActions = (triAttrs.browserActions ?? activeTask?.browserActions) as
      | string
      | undefined;
    if (rawBrowserActions && rawBrowserActions.trim().length > 0) {
      let parsed: unknown = null;
      try {
        parsed = JSON.parse(rawBrowserActions);
      } catch (firstErr) {
        // bpmn-js' XML attribute round-trip occasionally hands back an
        // HTML-entity-encoded payload (`&#34;` instead of `"`, `&amp;` etc.)
        // for custom moddle attributes containing JSON. Try decoding common
        // entities before giving up on the walkthrough.
        try {
          parsed = JSON.parse(decodeXmlEntities(rawBrowserActions));
        } catch {
          // eslint-disable-next-line no-console
          console.warn(
            "tri:browserActions ignored — JSON parse failed; running bot without walkthrough",
            { error: (firstErr as Error).message, raw: rawBrowserActions.slice(0, 80) },
          );
        }
      }
      if (parsed != null) {
        const result = BrowserActionListSchema.safeParse(parsed);
        if (!result.success) {
          // eslint-disable-next-line no-console
          console.warn(
            "tri:browserActions ignored — schema mismatch; running bot without walkthrough",
            { issues: result.error.issues },
          );
        } else {
          browserActions = result.data;
        }
      }
    }
  }

  const taskId = String(activeTask?._id ?? activeTask?.taskId ?? activeTask?.id ?? `t-${Date.now().toString(36)}`);
  const processInstanceId = String(
    activeTask?.processInstanceId ??
      activeTask?.instanceId ??
      activeTask?.processInstance ??
      `pi-${Date.now().toString(36)}`,
  );

  const sessionContext = {
    userId: authentication?.id ?? "anonymous",
    tenantId: authentication?.customer ?? "unknown-tenant",
    locale: typeof navigator !== "undefined" ? navigator.language || "de-DE" : "de-DE",
    timezone:
      typeof Intl !== "undefined"
        ? Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Berlin"
        : "Europe/Berlin",
  };

  // Merge session context into processVariables under `__session.*` so
  // the bot's prompt-stringifier exposes them to the LLM. This replaces
  // the old `kind: "context"` binding path — the model now resolves
  // session fields the same way it resolves any other description-based
  // value (against the process-data block).
  const mergedProcessVariables: Record<string, unknown> = {
    ...(processVariables ?? {}),
    __session: sessionContext,
  };

  const task: TaskAssignment = {
    taskId,
    processInstanceId,
    instruction,
    processVariables: mergedProcessVariables,
    context: sessionContext,
    callback: { url: cfg.callbackUrl, token: cfg.callbackToken },
    llmProxy: { baseUrl: cfg.llmProxyBaseUrl, sessionToken },
    approvalPolicy: cfg.approvalPolicy,
    ...(browserActions ? { browserActions } : {}),
  };

  return await new Promise<ClientBotSubmitResult>((resolve) => {
    try {
      chromeRuntime.sendMessage(
        cfg.extensionId,
        { kind: "submitTask", task },
        (reply: any) => {
          const lastError = chromeRuntime.lastError;
          if (lastError) {
            resolve({ ok: false, error: lastError.message ?? "chrome.runtime.lastError" });
            return;
          }
          if (!reply || typeof reply !== "object") {
            resolve({ ok: false, error: "No reply from extension." });
            return;
          }
          if (reply.kind === "accepted") {
            resolve({ ok: true, runId: reply.runId });
          } else if (reply.kind === "error") {
            resolve({ ok: false, error: `${reply.code}: ${reply.message}` });
          } else {
            resolve({ ok: false, error: `Unexpected reply: ${JSON.stringify(reply)}` });
          }
        },
      );
    } catch (e: any) {
      resolve({ ok: false, error: e?.message ?? String(e) });
    }
  });
}

export interface ClientBotApprovalDecision {
  runId: string;
  callId: string;
  decision: "approve" | "deny";
  reason?: string;
}

export interface ClientBotApprovalResult {
  ok: boolean;
  error?: string;
}

/**
 * Forward an approval decision to the locally-installed browser-bot
 * extension. Used by the ChatExchange approval-card UI when the user
 * clicks Approve/Deny on an `approval_requested` event from a run with
 * `approvalPolicy: "ask"`.
 *
 * Mirrors the pattern in `apps/test-client/src/transport.ts:approveBrowser`
 * — wraps the decision in `{ kind: "approve", decision }` and sends via
 * `chrome.runtime.sendMessage` to the configured extension id.
 */
export async function sendBrowserBotApproval(
  decision: ClientBotApprovalDecision,
): Promise<ClientBotApprovalResult> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const chromeRuntime = (globalThis as any).chrome?.runtime;
  if (!chromeRuntime?.sendMessage) {
    return { ok: false, error: "Chrome extension runtime not available." };
  }
  const cfg = getClientBotConfig();
  if (!cfg.extensionId) {
    return { ok: false, error: "REACT_APP_TRI_BOT_EXTENSION_ID is not configured." };
  }
  return await new Promise<ClientBotApprovalResult>((resolve) => {
    try {
      chromeRuntime.sendMessage(
        cfg.extensionId,
        { kind: "approve", decision },
        (reply: any) => {
          const lastError = chromeRuntime.lastError;
          if (lastError) {
            resolve({ ok: false, error: lastError.message ?? "chrome.runtime.lastError" });
            return;
          }
          if (reply?.kind === "error") {
            resolve({ ok: false, error: `${reply.code}: ${reply.message}` });
            return;
          }
          // The extension currently replies with `{kind: "accepted", ...}` or
          // an empty ack — both count as success.
          resolve({ ok: true });
        },
      );
    } catch (e: any) {
      resolve({ ok: false, error: e?.message ?? String(e) });
    }
  });
}
